(function() {
  'use strict';

  /**
   * Collect images from all 20 image inputs
   * @param {Object} sectionSettings - Section settings object
   * @returns {Array<string>} Array of image URLs
   */
  function collectImages(sectionSettings) {
    const images = [];
    for (let i = 1; i <= 20; i++) {
      const imageId = `review_image_${i}`;
      const imageUrl = sectionSettings[imageId];
      if (imageUrl && imageUrl.trim() !== '') {
        images.push(imageUrl);
      }
    }
    return images;
  }

  /**
   * Collect review content from all 20 content inputs
   * @param {Object} sectionSettings - Section settings object
   * @returns {Array<string>} Array of review texts
   */
  function collectReviewContents(sectionSettings) {
    const contents = [];
    for (let i = 1; i <= 20; i++) {
      const contentId = `review_content_${i}`;
      const content = sectionSettings[contentId];
      if (content && content.trim() !== '') {
        contents.push(content.trim());
      }
    }
    return contents;
  }

  /**
   * Parse reviewer names from textarea
   * @param {string} reviewerNamesText - Textarea content with newline-separated names
   * @returns {Array<string>} Array of reviewer names
   */
  function parseReviewerNames(reviewerNamesText) {
    if (!reviewerNamesText || reviewerNamesText.trim() === '') {
      return [];
    }
    return reviewerNamesText
      .split('\n')
      .map(name => name.trim())
      .filter(name => name !== '');
  }

  /**
   * Generate random rating between 4.5 and 5.0
   * @returns {number} Random rating (increment of 0.25)
   */
  function generateRandomRating() {
    const min = 4.5;
    const max = 5.0;
    const step = 0.25;
    const steps = (max - min) / step;
    const randomStep = Math.floor(Math.random() * (steps + 1));
    return min + (randomStep * step);
  }

  /**
   * Get random item from array
   * @param {Array} array - Array to select from
   * @returns {*} Random item from array
   */
  function getRandomItem(array) {
    if (array.length === 0) {
      return null;
    }
    const randomIndex = Math.floor(Math.random() * array.length);
    return array[randomIndex];
  }

  /**
   * Generate randomized reviews
   * @param {Object} sectionSettings - Section settings object
   * @returns {Array<Object>} Array of review objects
   */
  function generateRandomizedReviews(sectionSettings) {
    const images = collectImages(sectionSettings);
    const contents = collectReviewContents(sectionSettings);
    const reviewerNamesText = sectionSettings.reviewer_names || '';
    const names = parseReviewerNames(reviewerNamesText);
    const reviewsCount = parseInt(sectionSettings.reviews_count) || 6;

    // Validate we have at least one item in each category
    if (images.length === 0 || contents.length === 0 || names.length === 0) {
      console.warn('Random Review: Insufficient data. Need at least one image, one content, and one reviewer name.');
      return [];
    }

    const reviews = [];
    for (let i = 0; i < reviewsCount; i++) {
      const review = {
        image: getRandomItem(images),
        text: getRandomItem(contents),
        name: getRandomItem(names),
        rating: generateRandomRating()
      };
      reviews.push(review);
    }

    return reviews;
  }

  /**
   * Get section settings from theme editor
   * @param {string} sectionId - Section ID
   * @returns {Object|null} Section settings object
   */
  function getSectionSettings(sectionId) {
    if (typeof window.Shopify === 'undefined' || !window.Shopify.designMode) {
      return null;
    }

    try {
      // Try to get settings from Shopify theme editor
      const sectionElement = document.getElementById(sectionId);
      if (!sectionElement) {
        return null;
      }

      // Get settings from data attributes or theme editor API
      // This is a simplified approach - actual implementation may vary
      const settings = {};
      
      // Try to read from theme editor's section data
      if (window.Shopify.designMode && window.Shopify.designMode.editor) {
        const sectionData = window.Shopify.designMode.editor.sections[sectionId];
        if (sectionData && sectionData.settings) {
          return sectionData.settings;
        }
      }

      // Fallback: Try to read from DOM
      const sectionDataElement = sectionElement.querySelector('[data-section-settings]');
      if (sectionDataElement) {
        try {
          return JSON.parse(sectionDataElement.textContent);
        } catch (e) {
          console.warn('Could not parse section settings from DOM');
        }
      }

      return null;
    } catch (error) {
      console.error('Error getting section settings:', error);
      return null;
    }
  }

  /**
   * Update randomized_data setting in theme editor
   * @param {string} sectionId - Section ID
   * @param {Array<Object>} reviews - Randomized reviews array
   */
  function updateRandomizedData(sectionId, reviews) {
    if (typeof window.Shopify === 'undefined' || !window.Shopify.designMode) {
      return;
    }

    try {
      const reviewsJson = JSON.stringify(reviews);
      
      // Try to update via theme editor API
      if (window.Shopify.designMode && window.Shopify.designMode.editor) {
        const editor = window.Shopify.designMode.editor;
        
        // Update the setting
        if (editor.updateSection) {
          editor.updateSection(sectionId, {
            randomized_data: reviewsJson
          });
          return;
        }
      }

      // Fallback: Try to find and update the textarea directly
      const sectionElement = document.getElementById(sectionId);
      if (sectionElement) {
        // Find the randomized_data textarea in the settings panel
        // This is a workaround since we can't directly access settings panel
        const settingsPanel = document.querySelector('.shopify-section-editor__settings');
        if (settingsPanel) {
          const textarea = settingsPanel.querySelector('[name*="randomized_data"]');
          if (textarea) {
            textarea.value = reviewsJson;
            // Trigger input event to notify theme editor
            textarea.dispatchEvent(new Event('input', { bubbles: true }));
            textarea.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      }
    } catch (error) {
      console.error('Error updating randomized data:', error);
    }
  }

  /**
   * Perform randomization for a section
   * @param {string} sectionId - Section ID
   */
  function performRandomization(sectionId) {
    const settings = getSectionSettings(sectionId);
    if (!settings) {
      console.warn('Random Review: Could not get section settings for', sectionId);
      return;
    }

    // Check if randomized_data already exists and section hasn't changed
    // This prevents re-randomization on every load
    if (settings.randomized_data && settings.randomized_data.trim() !== '') {
      // Check if we should re-randomize (e.g., if inputs have changed)
      // For now, we'll re-randomize on each save to ensure fresh combinations
    }

    const reviews = generateRandomizedReviews(settings);
    if (reviews.length > 0) {
      updateRandomizedData(sectionId, reviews);
    }
  }

  /**
   * Initialize randomization on section load/save
   */
  function initRandomReview() {
    // Check if we're in the theme editor
    if (typeof window.Shopify === 'undefined' || !window.Shopify.designMode) {
      return;
    }

    // Listen for section load events (fires when section is loaded or saved)
    document.addEventListener('shopify:section:load', function(event) {
      const sectionId = event.detail.sectionId;
      if (sectionId && sectionId.includes('random-review')) {
        // Small delay to ensure settings are available
        setTimeout(function() {
          performRandomization(sectionId);
        }, 200);
      }
    });

    // Also listen for section reorder (which triggers a save)
    document.addEventListener('shopify:section:reorder', function(event) {
      const sectionId = event.detail.sectionId;
      if (sectionId && sectionId.includes('random-review')) {
        setTimeout(function() {
          performRandomization(sectionId);
        }, 200);
      }
    });
  }

  // Export functions for potential external use
  window.RandomReview = {
    generateRandomizedReviews: generateRandomizedReviews,
    collectImages: collectImages,
    collectReviewContents: collectReviewContents,
    parseReviewerNames: parseReviewerNames,
    generateRandomRating: generateRandomRating,
    performRandomization: performRandomization
  };

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initRandomReview);
  } else {
    initRandomReview();
  }
})();

